import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { HasOnError, replaceInChain } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import {
    getCartState,
    getCurrentCartState,
    isCartLoadedWithLines,
    isCartLoadedWithoutLines,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCart";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import {
    getCurrentPromotionsDataView,
    getPromotionsDataView,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import {
    chain,
    ExecuteOnSuccessCallback,
    WaitForData,
} from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";

const getCheckoutShippingCartState = (state: Parameters<typeof getCartState>[0], cartId?: string) =>
    cartId ? getCartState(state, cartId) : getCurrentCartState(state);

const hasCartLoadError = (cartState: ReturnType<typeof getCartState>) =>
    cartState.errorStatusCode === 403 || cartState.errorStatusCode === 404;

replaceInChain(chain, WaitForData, async props => {
    const checkData = () => {
        const state = props.getState();
        if (!getCurrentCountries(state)) {
            return false;
        }
        if (!getAddressFieldsDataView(state).value) {
            return false;
        }

        if (props.parameter.cartId) {
            const cartState = getCartState(state, props.parameter.cartId);
            if (hasCartLoadError(cartState)) {
                return true;
            }

            const promotions = getPromotionsDataView(state, props.parameter.cartId);
            if (!promotions || promotions.isLoading) {
                return false;
            }

            if (!isCartLoadedWithLines(cartState)) {
                if (isCartLoadedWithoutLines(cartState)) {
                    props.dispatch(loadCart({ cartId: props.parameter.cartId, getPromotions: true }));
                }
                return false;
            }
        } else {
            const currentCartState = getCurrentCartState(state);
            if (hasCartLoadError(currentCartState)) {
                return true;
            }

            const currentPromotions = getCurrentPromotionsDataView(state);
            if (!currentPromotions || currentPromotions.isLoading) {
                return false;
            }

            if (!isCartLoadedWithLines(currentCartState)) {
                if (isCartLoadedWithoutLines(currentCartState)) {
                    props.dispatch(loadCurrentCart({ getPromotions: true }));
                }
                return false;
            }
        }

        return true;
    };

    await waitFor(checkData);
});

replaceInChain(chain, ExecuteOnSuccessCallback, props => {
    const state = props.getState();
    const cartState = getCheckoutShippingCartState(state, props.parameter.cartId);

    if (hasCartLoadError(cartState) || !isCartLoadedWithLines(cartState)) {
        props.dispatch({
            type: "Pages/CheckoutShipping/SetIsPreloadingData",
            isPreloadingData: false,
        });

        const errorMessage =
            cartState.errorStatusCode === 404
                ? (siteMessage("Cart_CartNotFound") as string)
                : (siteMessage("Forbidden") as string);

        (props.parameter as typeof props.parameter & HasOnError<string>).onError?.(errorMessage);
        return false;
    }

    (props.parameter.onSuccess as (cartLoaded?: boolean) => void)(true);
});
